import React, { useEffect, useState } from "react";
import {
  getCategoryModalCopy,
  getCategoryPresetPickerCopy,
  CATEGORY_SEE_ALL_CREDIT_TEXT,
} from "../../config/dashboard/dashboardCategoryModalConfig";
import { getCategoryDemoSamplePool } from "../../config/dashboard/demoAssetsConfig";
import { useRandomDemoSamples } from "../../hooks/dashboard/useRandomDemoSamples";
import PresetSelectDropdown from "../../components/ui/PresetSelectDropdown";
import { ModalUploadLegalNotice } from "./UploadLegalNotice";
import "../../components/ui/CategorySeeAllModal.css";
import "./TrendingToolUploadModal.css";

const CategorySeeAllModal = ({
  category,
  presets = [],
  isOpen,
  loading = false,
  onClose,
  onUpload,
  onSampleClick,
  isProcessingSample = false,
}) => {
  const [selectedPreset, setSelectedPreset] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setSelectedPreset(null);
    }
  }, [isOpen, category?.category_id]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const categorySamplePool = getCategoryDemoSamplePool(category?.category_id);
  const categorySamples = useRandomDemoSamples(categorySamplePool, {
    enabled: isOpen && Boolean(category?.category_id),
    resetKey: category?.category_id,
  });

  if (!isOpen || !category) return null;

  const categoryDefault = category.default_display_label || "";
  const { title: modalTitle, description: modalDescription } = getCategoryModalCopy(category);
  const pickerCopy = getCategoryPresetPickerCopy(category);

  const handleUploadClick = () => {
    if (!selectedPreset) return;
    onUpload(selectedPreset, category.category_id);
    onClose();
  };

  return (
    <div className="v1-see-all-modal-overlay" onClick={onClose}>
      <div
        className="v1-trending-upload-modal-card"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="v1-trending-upload-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <div className="v1-trending-upload-modal-body">
          <h2 className="v1-trending-upload-modal-title">{modalTitle}</h2>
          {modalDescription ? (
            <p className="v1-trending-upload-modal-description">{modalDescription}</p>
          ) : null}

          <div className="v1-trending-upload-card v1-category-see-all-card">
            <PresetSelectDropdown
              id="category-preset-select"
              label={pickerCopy.label}
              presets={presets}
              selectedPreset={selectedPreset}
              onSelect={setSelectedPreset}
              loading={loading}
              pickerCopy={pickerCopy}
              categoryId={category.category_id}
              categoryDefault={categoryDefault}
            />

            <div className="v1-category-upload-actions">
              <button
                type="button"
                className="v1-trending-upload-btn"
                onClick={handleUploadClick}
                disabled={!selectedPreset || loading}
              >
                <span className="v1-trending-upload-btn-icon" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M8 14l3-3 2 2 3-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 3v4M10 5h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </span>
                Upload Image
              </button>
              <p className="v1-trending-upload-hint v1-category-upload-hint">
                or drag and drop images · Paste image or <kbd>Ctrl + V</kbd>
              </p>
              <p className="v1-trending-upload-credit v1-category-see-all-credit">
                {CATEGORY_SEE_ALL_CREDIT_TEXT}
              </p>
            </div>
          </div>

          <div className="v1-trending-upload-samples">
            <p className="v1-trending-upload-samples-label">
              No image? Try one of these:
            </p>
            <div className="v1-trending-upload-samples-row">
              {categorySamples.map((sample, index) => {
                const thumbClassName =
                  "v1-trending-upload-sample-thumb" +
                  (onSampleClick ? "" : " v1-trending-upload-sample-thumb--preview");

                if (onSampleClick) {
                  return (
                    <button
                      key={`${sample.id || sample.alt}-${index}`}
                      type="button"
                      className={thumbClassName}
                      onClick={() =>
                        onSampleClick(sample, {
                          categoryId: category?.category_id,
                          preset: selectedPreset,
                        })
                      }
                      disabled={isProcessingSample}
                      aria-label={sample.alt}
                    >
                      <img src={sample.src} alt={sample.alt} decoding="async" loading="lazy" />
                    </button>
                  );
                }

                return (
                  <div
                    key={`${sample.alt}-${index}`}
                    className={thumbClassName}
                    aria-hidden="true"
                  >
                    <img src={sample.src} alt="" decoding="async" loading="lazy" />
                  </div>
                );
              })}
            </div>
          </div>

          <ModalUploadLegalNotice />
        </div>
      </div>
    </div>
  );
};

export default CategorySeeAllModal;
